/** The workspace's middle column: the original message, desk actions, internal notes and the Reply panel. */
import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError } from '../../api/client'
import { fmtDateTime } from '../../lib/format'
import { TITLE_LABELS, stateLabel, topicLabel, topicTone, waitingLabel, type DeskChannel, type StaffMember } from '../../lib/supportDesk'
import type { DeskConversationDetail } from '../../lib/supportWorkspace'
import { AttachmentList } from '../AttachmentList'
import { ReplyPanel } from '../ReplyPanel'
import { ConversationActions } from './ConversationActions'
import { ForwardPanel } from './ForwardPanel'
import { NotesPanel } from './NotesPanel'
import { Chip } from './QueueList'
import { SavedReplyPicker } from './SavedReplyPicker'

const CHANNEL_LABEL: Record<DeskChannel, string> = { contact: 'Contact form', feedback: 'Feedback', email: 'Email' }

type Props = {
  channel: DeskChannel
  id: number
  publication: string
  canAct: boolean
  staff: StaffMember[]
  meEmail: string | undefined
}

export function ConversationView({ channel, id, publication, canAct, staff, meEmail }: Props) {
  const [forwarding, setForwarding] = useState(false)
  const detail = useQuery<DeskConversationDetail>({
    queryKey: ['support', 'conversation', channel, id],
    queryFn: () => api.get<DeskConversationDetail>(`/api/support/conversations/${channel}/${id}`),
    retry: false,
  })

  if (detail.isLoading) return <Column><p className="text-paper-trace">Loading conversation…</p></Column>
  if (detail.isError || !detail.data) {
    const gone = detail.error instanceof ApiError && detail.error.status === 404
    return (
      <Column>
        <p className={gone ? 'text-paper-dim' : 'text-red-400'}>
          {gone ? "This conversation isn't on the desk. It may have been filtered out of the inbox." : "Couldn't load the conversation. Try again."}
        </p>
      </Column>
    )
  }
  const c = detail.data
  const replyTitle = c.publication ?? publication

  return (
    <Column>
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="font-serif text-2xl text-paper mr-1">{c.name || c.email || 'Unknown sender'}</h2>
        <Chip tone={c.member ? 'text-radar-green border-radar-green' : undefined}>{c.member ? 'Member' : 'Visitor'}</Chip>
        <Chip tone={topicTone(c.topic)}>{topicLabel(c.topic)}</Chip>
        <Chip>{stateLabel(c.state)}</Chip>
        {c.publication && c.publication !== publication && <Chip>{TITLE_LABELS[c.publication] ?? c.publication}</Chip>}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm text-paper-dim">
        <span>{c.email ?? 'No email address'}</span>
        <span>Owner: {c.assignee_email ?? 'unassigned'}</span>
        {c.snoozed_until && c.state === 'snoozed' && <span>Snoozed until {fmtDateTime(c.snoozed_until)}</span>}
      </div>
      {canAct && (
        <div className="flex flex-wrap items-center gap-2">
          <ConversationActions conv={c} staff={staff} meEmail={meEmail} />
          {!forwarding && (
            <button type="button" onClick={() => setForwarding(true)}
              className="label border border-ink-500 px-3 py-2 hover:border-amber-glow">Forward</button>
          )}
        </div>
      )}
      {canAct && forwarding && (
        <ForwardPanel channel={c.channel} id={c.message_id} publication={c.publication ?? (publication === 'none' ? null : publication)}
          onDone={() => setForwarding(false)} />
      )}

      <article className="border-l-2 border-radar-cyan pl-4 space-y-2">
        <div className="label text-radar-cyan">
          {c.source_label ?? CHANNEL_LABEL[c.channel]} · {fmtDateTime(c.received_at)}
          {c.state === 'to_review' && ` · waited ${waitingLabel(c.waiting_hours)}`}
        </div>
        {c.subject && <div className="text-paper font-semibold">{c.subject}</div>}
        <p className="text-paper text-[15px] leading-relaxed whitespace-pre-wrap break-words">{c.body || '(no text)'}</p>
        {c.attachments && <AttachmentList ownerId={c.message_id} attachments={c.attachments} base="/api/inbound/email-attachments" />}
      </article>

      <NotesPanel channel={c.channel} id={c.message_id} canAct={canAct} />

      <ReplyPanel key={`${c.channel}-${c.message_id}`} channel={c.channel} messageId={c.message_id}
        extraTools={<SavedReplyPicker publication={replyTitle} />} />
    </Column>
  )
}

function Column({ children }: { children: ReactNode }) {
  return <section aria-label="Conversation" className="flex flex-col gap-4 p-5 min-w-0">{children}</section>
}
